import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Icon from '../AppIcon';
import Button from './Button';
import usageTrackingService from '../../services/usageTracking';
import { getPlanConfig, getPlanLevel, normalizePlanSlug, PLAN_SLUGS } from '../../config/plans';

const NavigationHeader = ({ user = null, subscriptionTier = PLAN_SLUGS.TRIAL, demoMode = false }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { signOut, user: authUser, userProfile } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Get subscription tier from userProfile if not provided
  const currentTier = React.useMemo(() => {
    return normalizePlanSlug(subscriptionTier || userProfile?.subscription_tier);
  }, [subscriptionTier, userProfile?.subscription_tier]);

  // ✅ Google Ads conversion payante après retour Stripe (session_id)
  const purchaseConvFiredRef = useRef(false);
  useEffect(() => {
    const sessionId = searchParams?.get('session_id');
    if (!sessionId) return;
  
    // On déclenche seulement si l'abonnement est actif
    const tier = normalizePlanSlug(currentTier);
    if (tier === PLAN_SLUGS.TRIAL) return;
  
    // 🔐 Anti-double STRICT par session Stripe
    const key = `gtag:subscription_purchase:${sessionId}`;
    if (window.localStorage.getItem(key) === '1') return;
  
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      // ⚠️ Marquer AVANT pour éviter double fire (React StrictMode)
      window.localStorage.setItem(key, '1');
  
      // (optionnel) Event interne pour debug
      window.gtag('event', 'subscription_success', {
        session_id: sessionId,
        tier,
        user_id: authUser?.id
      });
  
      // ✅ CONVERSION GOOGLE ADS – PURCHASE
      window.gtag('event', 'conversion', {
        send_to: 'AW-16585798234/FWtzCJPT3fUbENrk3OQ9',
        transaction_id: sessionId
        // new_customer: true // optionnel si tu veux plus tard
      });
    }
  }, [searchParams, currentTier, authUser?.id]);

  // Load usage statistics (backend handles tier detection automatically)
  useEffect(() => {
    if (!authUser?.id) {
      setUsageStats(null);
      return;
    }

    const loadUsageStats = async () => {
      setLoadingUsage(true);
      try {
        // Backend function automatically uses the correct tier from user_profiles
        const stats = await usageTrackingService.getUsageStats(authUser.id);
        setUsageStats(stats);
      } catch (error) {
        console.error('Error loading usage stats:', error);
      } finally {
        setLoadingUsage(false);
      }
    };

    // Load immediately
    loadUsageStats();

    // Refresh every 30 seconds
    const interval = setInterval(loadUsageStats, 30000);

    return () => clearInterval(interval);
  }, [authUser?.id, location.pathname]); // Refresh when route changes

  const allNavigationItems = [
    { label: 'Tableau de bord', labelEn: 'Dashboard', path: '/main-dashboard', icon: 'LayoutDashboard', requiredTier: null },
    { label: 'Planification', labelEn: 'Planning', path: '/investment-planning', icon: 'TrendingUp', requiredTier: 'pro' },
  ];

  // Filter navigation items based on subscription tier
  const navigationItems = allNavigationItems.filter(item => {
    if (!item.requiredTier) return true;
    return getPlanLevel(currentTier) >= getPlanLevel(item.requiredTier);
  });

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleOverlayClick = () => {
    setIsMobileMenuOpen(false);
    setShowUserMenu(false);
  };

  const handleLogout = async () => {
    setShowUserMenu(false);
    setIsMobileMenuOpen(false);

    const { error } = await signOut();
    if (!error) {
      // Clear any stored data
      sessionStorage.clear();
      navigate('/login');
    } else {
      console.error('Logout error:', error);
      alert('Erreur lors de la déconnexion. Veuillez réessayer.');
    }
  };

  const getSubscriptionLabel = () => {
    return getPlanConfig(currentTier).name;
  };

  const formatUsageLimit = (limit) => {
    return limit === '∞' || limit === Infinity ? '∞' : limit;
  };

  // Calculate usage percentage and color
  const getUsageColor = (used, limit) => {
    if (limit === '∞' || limit === Infinity) return 'var(--color-success)';
    const limitNum = typeof limit === 'number' ? limit : parseInt(limit);
    if (limitNum === 0) return 'var(--color-muted-foreground)';
    const percentage = (used / limitNum) * 100;
    if (percentage >= 100) return 'var(--color-destructive)';
    if (percentage >= 75) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === '∞' || limit === Infinity) return 0;
    const limitNum = typeof limit === 'number' ? limit : parseInt(limit);
    if (limitNum === 0) return 0;
    return Math.min((used / limitNum) * 100, 100);
  };

  // Demo mode: simplified header with just logo and login button
  if (demoMode && !authUser) {
    return (
      <header className="navigation-header" style={{ justifyContent: 'space-between' }}>
        <Link to="/" className="navigation-header-logo">
          <img src="/immoiq-logo.png" alt="ImmoIQ" style={{ height: '32px', width: 'auto' }} />
          <span>ImmoIQ</span>
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
          <button
            type="button"
            onClick={toggleTheme}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              fontSize: '0.8125rem',
              fontWeight: 500
            }}
            className="rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors border border-transparent hover:border-border"
            title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
            aria-label={theme === 'dark' ? 'Passer au mode clair' : 'Passer au mode sombre'}
          >
            <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={18} />
            <span>{theme === 'dark' ? 'Clair' : 'Sombre'}</span>
          </button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/login')}
          >
            Se connecter
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/register')}
          >
            Créer un compte
          </Button>
        </div>
      </header>
    );
  }

  return (
    <>
      <header className="navigation-header">
        <Link to="/main-dashboard" className="navigation-header-logo">
          <img src="/immoiq-logo.png" alt="ImmoIQ" style={{ height: '32px', width: 'auto' }} />
          <span>ImmoIQ</span>
        </Link>

        <nav className="navigation-header-nav">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`navigation-header-link ${isActivePath(item?.path) ? 'active' : ''}`}
            >
              {item?.label}
            </Link>
          ))}
        </nav>

        <div className="navigation-header-actions">
          <button
            type="button"
            onClick={toggleTheme}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              fontSize: '0.8125rem',
              fontWeight: 500
            }}
            className="rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors border border-transparent hover:border-border"
            title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
            aria-label={theme === 'dark' ? 'Passer au mode clair' : 'Passer au mode sombre'}
          >
            <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={18} />
            <span>{theme === 'dark' ? 'Clair' : 'Sombre'}</span>
          </button>

          {/* Usage Stats - Compact View */}
          {usageStats && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-xs)',
              fontSize: '0.75rem',
              color: 'var(--color-muted-foreground)',
              fontFamily: 'var(--font-caption)',
              marginRight: 'var(--spacing-sm)',
              padding: '4px 8px',
              background: 'var(--color-muted)',
              borderRadius: 'var(--radius-sm)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: getUsageColor(usageStats.analyses.used, usageStats.analyses.limit)
              }}>
                <Icon name="FileText" size={12} />
                <span style={{ fontWeight: 600 }}>{usageStats.analyses.used}</span>
                <span style={{ opacity: 0.7 }}>/{formatUsageLimit(usageStats.analyses.limit)}</span>
              </div>
              {usageStats.aiImports && usageStats.aiImports.limit !== 0 && (
                <>
                  <span style={{ opacity: 0.3 }}>•</span>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: getUsageColor(usageStats.aiImports.used, usageStats.aiImports.limit)
                  }}>
                    <Icon name="Link" size={12} />
                    <span style={{ fontWeight: 600 }}>{usageStats.aiImports.used}</span>
                    <span style={{ opacity: 0.7 }}>/{formatUsageLimit(usageStats.aiImports.limit)}</span>
                  </div>
                </>
              )}
              {usageStats.aiAnalyses && usageStats.aiAnalyses.limit !== 0 && (
                <>
                  <span style={{ opacity: 0.3 }}>•</span>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: getUsageColor(usageStats.aiAnalyses.used, usageStats.aiAnalyses.limit)
                  }}>
                    <Icon name="Sparkles" size={12} />
                    <span style={{ fontWeight: 600 }}>{usageStats.aiAnalyses.used}</span>
                    <span style={{ opacity: 0.7 }}>/{formatUsageLimit(usageStats.aiAnalyses.limit)}</span>
                  </div>
                </>
              )}
            </div>
          )}

          <span className="navigation-header-subscription">
            {getSubscriptionLabel()}
          </span>

          <Button
            variant="outline"
            size="sm"
            iconName="Crown"
            iconPosition="left"
            onClick={() => navigate('/subscription-pricing')}
          >
            Améliorer
          </Button>

          <div style={{ position: 'relative' }}>
            <Button
              variant="ghost"
              size="icon"
              iconName="User"
              onClick={() => setShowUserMenu(!showUserMenu)}
            />
            {showUserMenu && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                background: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                minWidth: '200px',
                zIndex: 1000,
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  borderBottom: '1px solid var(--color-border)'
                }}>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'var(--color-foreground)',
                    margin: 0,
                    marginBottom: '4px'
                  }}>
                    {userProfile?.full_name || 'Utilisateur'}
                  </p>
                  <p style={{
                    fontFamily: 'var(--font-caption)',
                    fontSize: '0.75rem',
                    color: 'var(--color-muted-foreground)',
                    margin: 0,
                    marginBottom: 'var(--spacing-sm)'
                  }}>
                    {authUser?.email}
                  </p>

                  {usageStats && (
                    <div style={{
                      marginTop: 'var(--spacing-sm)',
                      paddingTop: 'var(--spacing-sm)',
                      borderTop: '1px solid var(--color-border)'
                    }}>
                      <p style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'var(--color-foreground)',
                        margin: '0 0 var(--spacing-xs) 0'
                      }}>
                        Utilisation
                      </p>

                      {/* Analyses */}
                      <div style={{ marginBottom: 'var(--spacing-xs)' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '2px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Icon name="FileText" size={12} />
                            <span style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              color: 'var(--color-foreground)'
                            }}>
                              Analyses
                            </span>
                          </div>
                          <span style={{
                            fontFamily: 'var(--font-caption)',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: getUsageColor(usageStats.analyses.used, usageStats.analyses.limit)
                          }}>
                            {usageStats.analyses.used}/{formatUsageLimit(usageStats.analyses.limit)}
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '4px',
                          background: 'var(--color-muted)',
                          borderRadius: '2px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${getUsagePercentage(usageStats.analyses.used, usageStats.analyses.limit)}%`,
                            height: '100%',
                            background: getUsageColor(usageStats.analyses.used, usageStats.analyses.limit),
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>

                      {/* AI Imports */}
                      {usageStats.aiImports && usageStats.aiImports.limit !== 0 && (
                        <div style={{ marginBottom: 'var(--spacing-xs)' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '2px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Icon name="Link" size={12} />
                              <span style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: 'var(--color-foreground)'
                              }}>
                                Import par URL
                              </span>
                            </div>
                            <span style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: getUsageColor(usageStats.aiImports.used, usageStats.aiImports.limit)
                            }}>
                              {usageStats.aiImports.used}/{formatUsageLimit(usageStats.aiImports.limit)}
                            </span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '4px',
                            background: 'var(--color-muted)',
                            borderRadius: '2px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${getUsagePercentage(usageStats.aiImports.used, usageStats.aiImports.limit)}%`,
                              height: '100%',
                              background: getUsageColor(usageStats.aiImports.used, usageStats.aiImports.limit),
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>
                      )}

                      {/* AI Analyses */}
                      {usageStats.aiAnalyses && usageStats.aiAnalyses.limit !== 0 && (
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '2px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Icon name="Sparkles" size={12} />
                              <span style={{
                                fontFamily: 'var(--font-caption)',
                                fontSize: '0.75rem',
                                color: 'var(--color-foreground)'
                              }}>
                                Analyse par IA
                              </span>
                            </div>
                            <span style={{
                              fontFamily: 'var(--font-caption)',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: getUsageColor(usageStats.aiAnalyses.used, usageStats.aiAnalyses.limit)
                            }}>
                              {usageStats.aiAnalyses.used}/{formatUsageLimit(usageStats.aiAnalyses.limit)}
                            </span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '4px',
                            background: 'var(--color-muted)',
                            borderRadius: '2px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${getUsagePercentage(usageStats.aiAnalyses.used, usageStats.aiAnalyses.limit)}%`,
                              height: '100%',
                              background: getUsageColor(usageStats.aiAnalyses.used, usageStats.aiAnalyses.limit),
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/settings');
                  }}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    background: 'transparent',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-xs)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    color: 'var(--color-foreground)',
                    transition: 'background var(--transition-base)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-muted)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Icon name="Settings" size={16} />
                  Paramètres
                </button>

                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    background: 'transparent',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-xs)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    color: 'var(--color-destructive)',
                    transition: 'background var(--transition-base)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-muted)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Icon name="LogOut" size={16} />
                  Déconnexion
                </button>
              </div>
            )}
          </div>
        </div>

        <button
          className="navigation-header-mobile-toggle"
          onClick={handleMobileMenuToggle}
          aria-label="Toggle mobile menu"
        >
          <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
        </button>
      </header>

      <div
        className={`navigation-header-mobile-overlay ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={handleOverlayClick}
      />

      <nav className={`navigation-header-mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-sm)',
            marginBottom: 'var(--spacing-md)',
            paddingBottom: 'var(--spacing-md)',
            borderBottom: '1px solid var(--color-border)'
          }}>
            <img src="/immoiq-logo.png" alt="ImmoIQ" style={{ height: '28px', width: 'auto' }} />
            <span style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 600,
              fontSize: '1.25rem',
              color: 'var(--color-foreground)'
            }}>
              ImmoIQ
            </span>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-md)'
          }}>
            <span style={{
              fontFamily: 'var(--font-caption)',
              fontSize: '0.875rem',
              color: 'var(--color-muted-foreground)'
            }}>
              Thème
            </span>
            <button
              type="button"
              onClick={toggleTheme}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 14px',
                background: 'var(--color-muted)',
                fontSize: '0.875rem'
              }}
              className="rounded-lg text-foreground transition-colors"
              aria-label={theme === 'dark' ? 'Passer au mode clair' : 'Passer au mode sombre'}
            >
              <Icon name={theme === 'dark' ? 'Sun' : 'Moon'} size={18} />
              <span>{theme === 'dark' ? 'Mode Clair' : 'Mode Sombre'}</span>
            </button>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-md)'
          }}>
            <span style={{
              fontFamily: 'var(--font-caption)',
              fontSize: '0.875rem',
              color: 'var(--color-muted-foreground)'
            }}>
              Abonnement
            </span>
            <span className="navigation-header-subscription">
              {getSubscriptionLabel()}
            </span>
          </div>

          {/* Usage Stats in Mobile Menu */}
          {usageStats && (
            <div style={{
              marginBottom: 'var(--spacing-md)',
              padding: 'var(--spacing-sm)',
              background: 'var(--color-muted)',
              borderRadius: 'var(--radius-md)'
            }}>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--color-foreground)',
                margin: '0 0 var(--spacing-xs) 0'
              }}>
                Utilisation
              </p>

              {/* Analyses */}
              <div style={{ marginBottom: 'var(--spacing-xs)' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '2px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Icon name="FileText" size={12} />
                    <span style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.75rem',
                      color: 'var(--color-foreground)'
                    }}>
                      Analyses
                    </span>
                  </div>
                  <span style={{
                    fontFamily: 'var(--font-caption)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: getUsageColor(usageStats.analyses.used, usageStats.analyses.limit)
                  }}>
                    {usageStats.analyses.used}/{formatUsageLimit(usageStats.analyses.limit)}
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '4px',
                  background: 'var(--color-background)',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${getUsagePercentage(usageStats.analyses.used, usageStats.analyses.limit)}%`,
                    height: '100%',
                    background: getUsageColor(usageStats.analyses.used, usageStats.analyses.limit),
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>

              {/* AI Imports */}
              {usageStats.aiImports && usageStats.aiImports.limit !== 0 && (
                <div style={{ marginBottom: 'var(--spacing-xs)' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '2px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon name="Link" size={12} />
                      <span style={{
                        fontFamily: 'var(--font-caption)',
                        fontSize: '0.75rem',
                        color: 'var(--color-foreground)'
                      }}>
                        Import par URL
                      </span>
                    </div>
                    <span style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: getUsageColor(usageStats.aiImports.used, usageStats.aiImports.limit)
                    }}>
                      {usageStats.aiImports.used}/{formatUsageLimit(usageStats.aiImports.limit)}
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '4px',
                    background: 'var(--color-background)',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${getUsagePercentage(usageStats.aiImports.used, usageStats.aiImports.limit)}%`,
                      height: '100%',
                      background: getUsageColor(usageStats.aiImports.used, usageStats.aiImports.limit),
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}

              {/* AI Analyses */}
              {usageStats.aiAnalyses && usageStats.aiAnalyses.limit !== 0 && (
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '2px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon name="Sparkles" size={12} />
                      <span style={{
                        fontFamily: 'var(--font-caption)',
                        fontSize: '0.75rem',
                        color: 'var(--color-foreground)'
                      }}>
                        Analyse par IA
                      </span>
                    </div>
                    <span style={{
                      fontFamily: 'var(--font-caption)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: getUsageColor(usageStats.aiAnalyses.used, usageStats.aiAnalyses.limit)
                    }}>
                      {usageStats.aiAnalyses.used}/{formatUsageLimit(usageStats.aiAnalyses.limit)}
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '4px',
                    background: 'var(--color-background)',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${getUsagePercentage(usageStats.aiAnalyses.used, usageStats.aiAnalyses.limit)}%`,
                      height: '100%',
                      background: getUsageColor(usageStats.aiAnalyses.used, usageStats.aiAnalyses.limit),
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}
            </div>
          )}

          <Button
            variant="default"
            fullWidth
            iconName="Crown"
            iconPosition="left"
            onClick={() => {
              handleMobileMenuClose();
              navigate('/subscription-pricing');
            }}
          >
            Améliorer le plan
          </Button>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-xs)',
          marginBottom: 'var(--spacing-lg)'
        }}>
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`navigation-header-link ${isActivePath(item?.path) ? 'active' : ''}`}
              onClick={handleMobileMenuClose}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)',
                padding: 'var(--spacing-sm)',
                borderRadius: 'var(--radius-md)',
                textDecoration: 'none'
              }}
            >
              <Icon name={item?.icon} size={20} />
              <span>{item?.label}</span>
            </Link>
          ))}
        </div>

        <div style={{
          paddingTop: 'var(--spacing-md)',
          borderTop: '1px solid var(--color-border)'
        }}>
          <div style={{
            padding: 'var(--spacing-sm)',
            marginBottom: 'var(--spacing-sm)'
          }}>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--color-foreground)',
              margin: 0,
              marginBottom: '4px'
            }}>
              {userProfile?.full_name || 'Utilisateur'}
            </p>
            <p style={{
              fontFamily: 'var(--font-caption)',
              fontSize: '0.75rem',
              color: 'var(--color-muted-foreground)',
              margin: 0
            }}>
              {authUser?.email}
            </p>
          </div>

          <Button
            variant="ghost"
            fullWidth
            iconName="Settings"
            iconPosition="left"
            onClick={() => {
              handleMobileMenuClose();
              navigate('/settings');
            }}
          >
            Paramètres
          </Button>

          <Button
            variant="ghost"
            fullWidth
            iconName="LogOut"
            iconPosition="left"
            onClick={handleLogout}
            style={{ color: 'var(--color-destructive)' }}
          >
            Déconnexion
          </Button>
        </div>
      </nav>
    </>
  );
};

export default NavigationHeader;
